import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'components/ui/panel';
import { Skeleton } from 'components/ui/skeleton';
import { formatDayMonth, formatGroupedNumber } from 'lib/dateFormat';
import type { DashboardActivity } from 'modules/analytics';
import { dayKeyToDate } from '../analyticsFormat';
import { FlatBarChart, type FlatBar } from './flat-bar-chart';

interface Props {
  activity: DashboardActivity[];
  isLoading: boolean;
  /** The reporting period as words ("So'nggi 30 kun"); replaced by the dates shown when the series is cut. */
  periodLabel: string;
  /** Plot height; phones draw a shorter chart. */
  chartHeight?: number;
}

/** One bar per day for a month; longer series keep their latest days. */
const MAX_BARS = 30;

/** Daily attempts as flat bars: the busiest day in ink, the rest quiet grey. */
export const SubmissionsBarChart: React.FC<Props> = ({ activity, isLoading, periodLabel, chartHeight = 128 }) => {
  const { t } = useTranslation();

  const shown = useMemo(() => activity.slice(-MAX_BARS), [activity]);
  const total = useMemo(() => shown.reduce((sum, day) => sum + day.count, 0), [shown]);
  const peak = useMemo(
    () => shown.reduce<DashboardActivity | null>((best, day) => (day.count > (best?.count ?? 0) ? day : best), null),
    [shown],
  );
  const bars = useMemo<FlatBar[]>(
    () =>
      shown.map((day) => ({
        key: day.date,
        value: day.count,
        label: formatDayMonth(dayKeyToDate(day.date)),
        valueLabel: t('analytics_n_submissions', { n: formatGroupedNumber(day.count) }),
      })),
    [shown, t],
  );

  // A cut series (all-time) no longer covers the named period: say which days it shows.
  const cut = activity.length > shown.length;
  const aside =
    cut && shown.length > 0
      ? `${formatDayMonth(dayKeyToDate(shown[0].date))} – ${formatDayMonth(dayKeyToDate(shown[shown.length - 1].date))}`
      : periodLabel;
  const axis = bars.length > 2 ? [bars[0], bars[Math.floor((bars.length - 1) / 2)], bars[bars.length - 1]] : bars;

  return (
    <Panel title={t('submissions')} aside={<span className="tabular-nums">{aside}</span>}>
      {isLoading ? (
        <div data-submissions-chart-loading className="space-y-4">
          <Skeleton className="h-7 w-24" />
          <Skeleton className="w-full" height={chartHeight} />
        </div>
      ) : total === 0 ? (
        <p className="flex items-center justify-center text-sm text-muted-foreground" style={{ height: chartHeight + 44 }}>
          {t('no_activity_this_week')}
        </p>
      ) : (
        <>
          <strong data-submission-total className="block text-2xl font-bold leading-none tracking-[-0.02em] tabular-nums text-foreground">
            {formatGroupedNumber(total)}
          </strong>
          <FlatBarChart
            className="mt-4"
            bars={bars}
            highlightKey={peak?.date}
            height={chartHeight}
            aria-label={t('analytics_submission_trend')}
          />
          <div aria-hidden className="mt-1.5 flex justify-between text-[11.5px] tabular-nums text-muted-foreground">
            {axis.map((bar) => (
              <span key={bar.key}>{bar.label}</span>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
};

export default SubmissionsBarChart;
